import { useCallback, useEffect, useState } from 'react';
import type { User } from 'oidc-client-ts';
import { userManager } from './oidc';
import { tokenYenileyiciAta } from './session';

export type AuthState =
  | { kind: 'loading' }
  | { kind: 'signed_out' }
  | { kind: 'signed_in'; user: User }
  | { kind: 'error'; message: string };

// StrictMode etkileri iki kez koşturur; signinCallback ise tek kullanımlıktır
// (state tüketilir, ikinci çağrı patlar). Modül seviyesinde tekilleştirilir.
let callbackOnce: Promise<User | undefined> | undefined;
function handleCallbackOnce(): Promise<User | undefined> {
  callbackOnce ??= userManager.signinCallback();
  return callbackOnce;
}

function isAuthCallback(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('state') && (params.has('code') || params.has('error'));
}

/**
 * Login durumu + login/logout eylemleri. Token'a UI dokunmaz; sadece taşır.
 *
 * ⚠️ **YENİLENEN JETON EKRANA BAĞLIDIR (işlevsel düzeltme turu — B/a).**
 * `automaticSilentRenew` gün 1'den beri açıktı ve jeton arka planda gerçekten
 * yenileniyordu; ama bu kanca yalnız AÇILIŞTA okuyordu — React durumu ilk
 * jetonda **donuyor**, `AccessTokenContext` yenisini hiç görmüyordu. Access
 * token ömrü dolunca (Keycloak varsayılanı 5 dk) her istek 401 alıyor, API
 * loglarına `exp claim timestamp check failed` düşüyordu. `addUserLoaded`
 * aboneliği bu kopuk halkayı kapatır: yenilenen her jeton doğrudan bağlama
 * geçer.
 *
 * ⚠️ **YENİLEME BAŞARISIZSA KABUK UNMOUNT EDİLMEZ.** `signed_out`a düşmek
 * ekranı LoginScreen ile değiştirir ve kullanıcının **yarım doldurduğu form
 * ekrandan silinir** — sahadaki kullanıcı o formu bir daha doldurmaz. Bunun
 * yerine `oturumHatasi` bayrağı kalkar; kabuk yerinde kalır, üstte görünür
 * bir uyarı çıkar ve yeniden giriş kararı KULLANICININDIR.
 */
export function useAuth(): {
  state: AuthState;
  /** Sessiz yenileme başarısız oldu / oturum IdP'de kapandı (veri korunur). */
  oturumHatasi: boolean;
  login: () => void;
  logout: () => void;
} {
  const [state, setState] = useState<AuthState>({ kind: 'loading' });
  const [oturumHatasi, setOturumHatasi] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const resolve = async (): Promise<AuthState> => {
      if (isAuthCallback()) {
        try {
          const user = await handleCallbackOnce();
          return user ? { kind: 'signed_in', user } : { kind: 'signed_out' };
        } finally {
          // code/state URL'de kalmasın (yenilemede tekrar tüketilmeye çalışılır)
          window.history.replaceState({}, '', window.location.pathname);
        }
      }
      const user = await userManager.getUser();
      return user && !user.expired ? { kind: 'signed_in', user } : { kind: 'signed_out' };
    };

    resolve()
      .then((next) => {
        if (!cancelled) setState(next);
      })
      .catch((error: unknown) => {
        if (!cancelled)
          setState({
            kind: 'error',
            message: error instanceof Error ? error.message : 'Bilinmeyen kimlik hatası',
          });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // JETON OLAYLARI — sessiz yenilemenin ekranla halkası (B/a).
  useEffect(() => {
    const yuklendi = (user: User): void => {
      setState({ kind: 'signed_in', user });
      setOturumHatasi(false);
    };
    const kopdu = (): void => setOturumHatasi(true);
    userManager.events.addUserLoaded(yuklendi);
    userManager.events.addSilentRenewError(kopdu);
    userManager.events.addUserSignedOut(kopdu);
    return () => {
      userManager.events.removeUserLoaded(yuklendi);
      userManager.events.removeSilentRenewError(kopdu);
      userManager.events.removeUserSignedOut(kopdu);
    };
  }, []);

  // SON ÇARE KANCASI (B/b): istek katmanı 401 alınca buradan yeniler.
  // `signinSilent` refresh token'ı kullanır; başarılıysa `addUserLoaded`
  // zaten yukarıdaki abonelikten geçer — ikinci bir durum yazımı yok.
  useEffect(() => {
    tokenYenileyiciAta(async () => {
      try {
        const user = await userManager.signinSilent();
        return user?.access_token ?? null;
      } catch {
        // Yenileme de başarısız: kullanıcıya GÖRÜNÜR bir yol açılır
        // (uyarı + "Yeniden giriş yap"). Kabuk YERİNDE KALIR — otomatik
        // yönlendirme, yazılmış form verisini ekrandan silerdi.
        setOturumHatasi(true);
        return null;
      }
    });
    return () => tokenYenileyiciAta(null);
  }, []);

  const login = useCallback(() => {
    void userManager.signinRedirect();
  }, []);
  const logout = useCallback(() => {
    void userManager.signoutRedirect();
  }, []);

  return { state, oturumHatasi, login, logout };
}
